import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, X, Send, Bot, User, Sparkles, Loader2, BarChart3, TrendingUp, Users } from 'lucide-react';
import { useAuth } from '../../AuthContext';
import configData from '../../configure';
import './AdminAssistant.css';

const AdminAssistant = ({ dashboardStats }) => {
    const { axios, user, token, orgId } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([
        { role: 'assistant', content: 'Hello Admin! I am your Institutional Intelligence Agent. How can I help you today?', timestamp: new Date() }
    ]);
    const [inputValue, setInputValue] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const handleSendMessage = async (e) => {
        if (e) e.preventDefault();
        if (!inputValue.trim()) return;

        const userMessage = { role: 'user', content: inputValue.trim(), timestamp: new Date() };
        setMessages(prev => [...prev, userMessage]);
        setInputValue('');
        setIsTyping(true);

        // Construct context-aware prompt
        const contextPrompt = `
You are an "Institutional Intelligence Agent" for the Educate school management platform. 
You are speaking to an Administrator. 

CURRENT INSTITUTIONAL DATA:
- Total Students: ${dashboardStats?.students || 'Loading...'}
- Total Faculty: ${dashboardStats?.faculty || 'Loading...'}
- Total Staff: ${dashboardStats?.staff || 'Loading...'}
- Total Courses: ${dashboardStats?.courses || 'Loading...'}
- Overall Attendance (Today): ${dashboardStats?.attendanceRate || 'N/A'}%

User Query: "${userMessage.content}"

Instructions: 
1. Use the data above to answer specifically. 
2. Be professional, concise, and helpful. 
3. If you don't have specific data, mention that you can help them navigate to the correct section.
4. If asked about trends, use the context to provide insights.
`;

        if (!user || !user.userId) {
            console.error('AdminAssistant: User not logged in or missing userId');
            setIsTyping(false);
            return;
        }

        const effectiveOrgId = orgId || user.orgId;
        if (!effectiveOrgId) {
            console.error('AdminAssistant: Missing orgId');
            setIsTyping(false);
            return;
        }

        try {
            console.log('AdminAssistant: Sending request to', configData.AI_API_URL);
            console.log('AdminAssistant: Context Prompt length:', contextPrompt.length);

            const response = await axios.post(
                `${configData.AI_API_URL}?userId=${user.userId}&orgId=${effectiveOrgId}`,
                contextPrompt,
                {
                    headers: {
                        'accept': 'application/hal+json'
                    }
                }
            );

            console.log('AdminAssistant: Response received:', response.status);
            const assistantMessage = {
                role: 'assistant',
                content: response.data.assistantReply || "I'm sorry, I couldn't process that request.",
                timestamp: new Date()
            };
            setMessages(prev => [...prev, assistantMessage]);
        } catch (error) {
            console.error('AI Assistant Connectivity Error:', {
                status: error.response?.status,
                data: error.response?.data,
                message: error.message,
                url: configData.AI_API_URL
            });
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: "I'm having trouble connecting to my brain right now. Please try again in a moment.",
                timestamp: new Date()
            }]);
        } finally {
            setIsTyping(false);
        }
    };

    return (
        <div className="admin-assistant-wrapper">
            {/* Toggle Button */}
            <button
                className={`assistant-toggle ${isOpen ? 'active' : ''}`}
                onClick={() => setIsOpen(!isOpen)}
            >
                {isOpen ? <X size={24} /> : <div className="bot-icon-wrapper"><Bot size={24} /><Sparkles className="sparkle-overlay" size={12} /></div>}
            </button>

            {/* Chat Window */}
            {isOpen && (
                <div className="assistant-window">
                    <div className="assistant-header">
                        <div className="header-info">
                            <Bot size={20} className="header-icon" />
                            <div>
                                <h4>Educate Intelligence</h4>
                                <span className="status-badge">Online</span>
                            </div>
                        </div>
                        <div className="header-actions">
                            <Sparkles size={16} className="sparkle-anim" />
                        </div>
                    </div>

                    <div className="messages-container">
                        {messages.map((msg, idx) => (
                            <div key={idx} className={`message-bubble ${msg.role}`}>
                                <div className="avatar">
                                    {msg.role === 'assistant' ? <Bot size={14} /> : <User size={14} />}
                                </div>
                                <div className="message-content">
                                    {msg.content}
                                    <span className="time">
                                        {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                </div>
                            </div>
                        ))}
                        {isTyping && (
                            <div className="message-bubble assistant typing">
                                <div className="avatar"><Bot size={14} /></div>
                                <div className="typing-indicator">
                                    <span></span><span></span><span></span>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    <form className="assistant-input-area" onSubmit={handleSendMessage}>
                        <input
                            type="text"
                            placeholder="Ask me about your school data..."
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                        />
                        <button type="submit" disabled={!inputValue.trim() || isTyping}>
                            {isTyping ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
                        </button>
                    </form>
                </div>
            )}
        </div>
    );
};

export default AdminAssistant;
